import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styles from './ConnectAccounts.module.scss'
import { ConnectionHubFromScreen, IIntegrationPartner, IpNames } from '../../../interfaces/interfaces';
import { RestS } from '../../../rest/rest-service';
import MeritoButton from '../../../components/common/MeritoButtons/MeritoButtons';
import { VscDebugDisconnect as ConnectIcon } from 'react-icons/vsc'
import CircularLoader from '../../../components/common/CircularLoader/CircularLoader';
import { useAppDispatch, useAppSelector } from '../../../redux/reduxHooks';
import { setRefreshIntegrationSuggestions } from '../../../redux/underwriting/underwriting';
import Connections from '../../Connections/Connections';
import useModal from '../../../components/Hooks/useModal';
import { useSearchParams } from 'react-router-dom';
import { UnderwritingFormContext } from '../context/UnderwritingFormContext';
import { IpRestS } from '../../../rest/ipRest-service';
import { useSpring, animated } from 'react-spring';
import { HiSearch as SearchIcon } from 'react-icons/hi'
import { RiCloseFill as CloseIcon } from 'react-icons/ri'
import { debounce } from 'lodash';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { MdArrowForward } from 'react-icons/md';
import { fetchSuggestedIps } from '../integrationSuggestions';




type Props = {
  onPendingChannelsChange?: (hasPendingChannels: boolean) => void;
}

const ConnectAccounts = ({ onPendingChannelsChange = () => { } }: Props) => {
  const [accountsData, setAccountsData] = useState([]);
  const [loading, setLoading] = useState(true);

  const { dfProgress } = useAppSelector(state => state.dataFetchingProgress);
  const { refreshIntegrationSuggestions } = useAppSelector(state => state.underwriting);
  const dispatch = useAppDispatch();
  const { toggle: connectionToggle, visible: connectionVisible } = useModal();

  const [searchParams, setSearchParams] = useSearchParams();
  // const { formData, formStepsValidity } = useContext(UnderwritingFormContext);
  const [connectedState, setConnectedState] = useState({
    takeState: false,
    ip: null,
    showIpScreen: false,
    fromScreen: null,
  });
  const [ipMappingData, setIpMappingData] = useState({});
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  // const animationProps = useSpring({ width: isSearchExpanded ? 600 : 40 });
  const animationProps = useSpring({
    transform: isSearchExpanded ? 'translateX(-0px)' : 'translateX(0px)',
    width: isSearchExpanded ? 600 : 40,
    // config: { tension: 0, friction: 20 }
  });

  const titleAnimation = useSpring({
    opacity: isSearchExpanded ? 0 : 1,
    maxHeight: isSearchExpanded ? 0 : 56, // Ensures smooth hiding
    config: { tension: 250, friction: 20 }
  });
  const searchInputRef = useRef(null);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');

  const debouncedSearch = useCallback(
    debounce((value) => setDebouncedSearchText(value), 300),
    []
  );

  useEffect(() => {
    if (isSearchExpanded) {
      searchInputRef.current?.focus();
    }
  }, [isSearchExpanded]);


  useEffect(() => {
    debouncedSearch(searchText);
  }, [searchText, debouncedSearch]);


  const formatIpObj = (ipArr: IIntegrationPartner[]) => {
    return ipArr.reduce((prev, curVal) => {
      prev[curVal?.companyName] = {
        ipName: curVal?.companyName,
        isSalesChannel: curVal?.isSalesChannel,
        ...curVal,
      }
      return prev;
    }, {})
  }

  const getDisplayRank = (item) => {
    if (item?.disconnectedAccounts?.length) {
      if (item?.isSalesChannel) return 0;
      if (item?.isAdsChannel) return 1;
      return 2;
    }
    return item?.isSuggested ? 3 : 4;
  }

  const fetchAccounts = async () => {
    try {
      const allIps = [IpNames.shopify, IpNames.metaAds, IpNames.googleAds, IpNames.googleAnalytics, IpNames.amazonSellerCentral, IpNames.amazonVendorCentral, IpNames.amazonAds];
      const adsChannels = new Set([IpNames.metaAds, IpNames.googleAds, IpNames.amazonAds]);
      const [{ accounts }, activeIpWorkspaces, suggestedIps] = await Promise.all([RestS.fetchAccessTokens() as any, IpRestS.getAllIpByStatus().then(res => res.data), fetchSuggestedIps()]);

      // console.log('active  workspaces', activeIpWorkspaces)

      // console.log('accounts', accounts)
      const groupedAcconts = accounts.reduce((prev, curVal) => {
        prev[curVal.ipName] = curVal;
        return prev;
      }, {});
      const ipMapping = activeIpWorkspaces.reduce((prev, curVal) => {
        if (curVal?.integrationPartner?.combined) {
          prev = {
            ...prev,
            ...formatIpObj(curVal?.integrationPartner?.optionalIp),
            ...formatIpObj(curVal?.integrationPartner?.mandatoryIp),
            ...formatIpObj(curVal?.integrationPartner?.nonMandatoryIp)
          }
        } else {
          prev = {
            ...prev,
            ...formatIpObj([curVal]),
          }
        }
        return prev;
      }, {});

      const sortedIps = [...new Set([...allIps, ...Object.keys(ipMapping)])];


      const allAccountsData = sortedIps.reduce((prev, curVal) => {
        const accountData = groupedAcconts[curVal] ?? { ipName: curVal, isConnected: false, accounts: [] };
        prev.push({
          ...accountData,
          ipLogo: ipMapping[curVal]?.logo,
          isSalesChannel: ipMapping[curVal]?.isSalesChannel,
          isAdsChannel: adsChannels.has(curVal as IpNames),
          isSuggested: suggestedIps.has(curVal)
        });
        return prev;
      }, []);

      // disconnected (sales, then ads) -> suggested -> everything else in the order built above
      allAccountsData.sort((a, b) => getDisplayRank(a) - getDisplayRank(b));

      console.log('allAccountsData', allAccountsData)
      setIpMappingData(ipMapping);
      setAccountsData(allAccountsData);
    } catch (error) {
      console.log('error', error);
    }
  }

  const clearSearchParam = () => {
    // console.log('searchParams', searchParams);
    console.log("RUNNING clear search params", searchParams)
    // const currentHash = window.location.hash;
    // Preserve the current hash

    if (searchParams.toString().length > 0) {
      searchParams.delete("customers");
      searchParams.delete("ip");
      searchParams.delete("step");
      searchParams.delete("success");
      setSearchParams(searchParams, { replace: true });

      // Check if there was a hash and it's been removed
      // if (currentHash && window.location.hash !== currentHash) {
      //     window.location.hash = currentHash; // Restore the hash manually
      // }
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchAccounts();
      setLoading(false);
    })();

    if (searchParams.get("connectionHub")) {
      console.log('searchParams')

      searchParams.delete("connectionHub");
      console.log('searchParams', searchParams.toString())
      connectionToggle();
      setSearchParams(searchParams, { replace: true });
    } else {
      console.log('connection hub not present')
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [dfProgress]);

  // the discovery scan finishes well after the page loads
  useEffect(() => {
    if (!refreshIntegrationSuggestions) return;
    fetchAccounts();
    dispatch(setRefreshIntegrationSuggestions(false));
  }, [refreshIntegrationSuggestions]);


  // useEffect(() => {
  //     if (!connectionVisible) {
  //         setConnectedState({
  //             takeState: false,
  //             ip: null,
  //             showIpScreen: false,
  //             fromScreen: null,
  //         })
  //     }
  // }, [connectionVisible]);

  const filteredAccountsData = useMemo(() => {
    if (!debouncedSearchText) return accountsData;
    const filtered = accountsData.filter(account =>
      account.ipName.toLowerCase().includes(debouncedSearchText.toLowerCase())
    );
    return filtered.length ? filtered : [];
  }, [debouncedSearchText, accountsData]);

  useEffect(() => {
    console.log('filteredAccountsData', filteredAccountsData);
  }, [filteredAccountsData])

  // a suggested channel only counts while the user hasn't connected it
  useEffect(() => {
    const hasPendingChannels = accountsData.some(item => {
      const connectedCount = (item?.accounts?.length ?? 0) + (item?.inProgressAccounts?.length ?? 0);
      return Boolean(item?.disconnectedAccounts?.length) || (item?.isSuggested && !connectedCount);
    });
    onPendingChannelsChange(hasPendingChannels);
  }, [accountsData])


  return (
    <div className={styles.connectAccountsRootContainer}>
      {loading ?
        <div className={styles.loadingContainer}>
          <CircularLoader />
        </div>
        : <div className={styles.connectAccountsWrapper}>
          {
            filteredAccountsData?.filter(item => item.disconnectedAccounts?.length)?.length ?
              <div className={styles.infoBanner}>
                <WarningAmberIcon />
                <p>Some accounts may be disconnected due to technical issues. Please reconnect.</p>
              </div> : null
          }
          <div className={styles.sectionHeadingContainer}>
            {/* {<div className={`${styles.title} ${isSearchExpanded ? styles.hidden : ''}`}>
                            Connect All Your Relevant Accounts
                        </div>} */}

            <animated.div style={titleAnimation} className={styles.titleContainer}>
              <div className={styles.title}>Connect Accounts</div>
              <div className={styles.subTitle}>Connect every channel your business runs on.</div>
            </animated.div>

            <div className={styles.searchContainer}>
              <animated.div style={animationProps} className={`${styles.searchBar} ${isSearchExpanded ? styles.expanded : ''}`}>
                <div className={`${styles.searchIcon} ${isSearchExpanded ? styles.moveLeft : ''}`}>
                  <SearchIcon />
                </div>
                <input ref={searchInputRef} className={styles.searchInput} type="text" placeholder="Search" value={searchText}
                  onChange={(e) => setSearchText(e.target.value)} />
                <div className={`${styles.searchBtn} ${isSearchExpanded ? styles.expanded : ''}`} onClick={() => {
                  setIsSearchExpanded(!isSearchExpanded);
                  // if (!isSearchExpanded) {
                  //     setTimeout(() => searchInputRef.current?.focus(), 100);
                  // } else {
                  setSearchText('');
                  // }
                }}>
                  {isSearchExpanded ? <CloseIcon /> : <SearchIcon />}
                </div>
              </animated.div>
            </div>
          </div>

          {filteredAccountsData?.length ? <div className={styles.accountsCardOuterContainer}>
            {filteredAccountsData.map((item) => {
              const connectedCount = (item?.accounts?.length ?? 0) + (item?.inProgressAccounts?.length ?? 0);
              const disconnectedCount = item?.disconnectedAccounts?.length ?? 0;
              const showConnectBtn = Boolean(disconnectedCount) || item?.isSuggested;
              const showNotConnected = !connectedCount && !disconnectedCount && !item?.isSuggested;

              const openConnectionHub = () => {
                setConnectedState({
                  takeState: true,
                  ip: ipMappingData[item.ipName],
                  showIpScreen: true,
                  fromScreen: ConnectionHubFromScreen.underwriting
                })
                connectionToggle()
              };

              return (
                <div className={styles.gridItemContainer} onClick={openConnectionHub}>
                  <div className={styles.channelsFrame}>
                    <div className={styles.ipLogoContainer}>
                      <img src={item.ipLogo} alt="logo" />
                    </div>
                    <div className={styles.ipText}>
                      {item?.ipName}
                    </div>
                  </div>

                  {connectedCount || disconnectedCount || showNotConnected ? <div className={styles.connectionStatusFrame}>
                    {connectedCount ? <div className={styles.connectedStatus}>
                      {connectedCount} connected
                    </div> : null}
                    {disconnectedCount ? <div className={`${styles.connectedStatus} ${styles.disconnected}`}>
                      {disconnectedCount} disconnected
                    </div> : null}
                    {showNotConnected ? <div className={`${styles.connectedStatus} ${styles.notConnected}`}>
                      Not connected
                    </div> : null}
                  </div> : null}

                  {item?.isSuggested ? <div className={styles.suggestedPill}>
                    <AutoAwesomeIcon />
                    <span>We found you here</span>
                  </div> : null}

                  {showConnectBtn ? <div className={styles.connectBtnContainer}>
                    <MeritoButton
                      type="primary-1"
                      size="sm"
                      isRounded
                      isIconToRight
                      btnText="Connect"
                      icon={<MdArrowForward />}
                      onClick={(e) => {
                        e.stopPropagation();
                        openConnectionHub();
                      }}
                    />
                  </div> : null}
                </div>
              )
            })}
          </div> : <div className={styles.coldTextContainer}>No channels match your search criteria.</div>}

        </div>}
      <Connections
        toggle={() => {
          connectionToggle();
          setConnectedState({
            takeState: false,
            ip: null,
            showIpScreen: false,
            fromScreen: null,
          })
        }}
        visible={connectionVisible}
        clearParams={clearSearchParam}
        connectedState={connectedState}
        onDisconnectRefresh={() => {
          fetchAccounts();
        }}
      />
    </div>
  );
}

export default ConnectAccounts;